import { useEffect } from "react";
import {
  dismissTopDismissible,
  dismissibleCount,
  subscribeDismissibles,
} from "./dismissibleStack";

/**
 * escapeToDismiss — Escape closes the top-most overlay, the same one Back does.
 *
 * THE PROBLEM
 * -----------
 * Every overlay in this app registers with `dismissibleStack`, and Android's
 * Back pops the top of it. On the web that stack had no input at all. Measured
 * on /map: with a collector preview open, a real Escape keydown left the sheet
 * on screen; only a tap on bare map closed it. The single Escape handler in the
 * codebase was a local one inside `MatchRevealMoment`, for that one dialog.
 *
 * That leaves a keyboard, switch or screen-reader user with no way to put an
 * overlay down. It is worst on the collector preview: on a 320x568 viewport the
 * sheet's only close button renders above the top of the screen (measured top
 * -93.8), so the pointer affordance is gone too and Escape was the only exit
 * that could have existed.
 *
 * WHY THE LISTENER LIVES HERE AND NOT IN dismissibleStack
 * -------------------------------------------------------
 * The stack's docblock says it is DOM-free on purpose — it is read from a
 * Capacitor callback that runs outside React, and it is the one thing both the
 * native and web dismissal paths agree on. Giving it a `document` reference
 * would make the registry depend on a DOM that is not guaranteed to exist when
 * the native path calls it. So the flow is one-way: this module imports the
 * stack, never the reverse.
 *
 * WHY THE LISTENER IS NOT ALWAYS ATTACHED
 * ---------------------------------------
 * Escape has other meanings on a page — leaving a native `<dialog>`, cancelling
 * an IME composition, aborting a drag, stopping a load. A permanently attached
 * document handler is a permanent claim on all of them, and it means every
 * Escape in a session walks through this code to decide it has nothing to do.
 * Subscribing to the stack's depth and attaching only while an overlay is up
 * makes the no-overlay case literally nothing: no listener, no branch, no
 * `preventDefault`, so Escape keeps whatever meaning the page gave it.
 */

/**
 * Handle one keydown. Returns true when the press was consumed, so a caller
 * (and a test) can see the decision rather than infer it from what moved.
 *
 * The guards, and why each one is here:
 *   - not Escape        — everything else on the keyboard is somebody else's.
 *   - `repeat`          — holding the key down fires a stream of keydowns. One
 *                         press should close one overlay, not unwind the whole
 *                         stack because a finger rested on the key.
 *   - `defaultPrevented`— an overlay that wants to own Escape itself (a
 *                         confirm-before-discard, a cropper mid-upload) calls
 *                         `preventDefault` and is left alone. Without this the
 *                         stack would close it out from under its own handler.
 *
 * `preventDefault` is called only on a press we actually acted on: consuming
 * the key is the correct signal to the browser that a modal took it, and it is
 * exactly what would be wrong to do when nothing was dismissed.
 */
export const handleEscapeKeydown = (event) => {
  if (!event || event.key !== "Escape") return false;
  if (event.repeat) return false;
  if (event.defaultPrevented) return false;
  if (!dismissTopDismissible()) return false;
  event.preventDefault();
  return true;
};

/**
 * Wire Escape to the stack on `target` (the document). Returns the uninstall.
 *
 * The listener is attached the moment the stack goes from empty to non-empty
 * and removed the moment it empties again — one attach per open, one detach per
 * close, never two at once. Two live listeners would be invisible right up
 * until a single Escape closed two overlays.
 */
export const installEscapeToDismiss = (
  target = typeof document === "undefined" ? null : document
) => {
  if (!target || typeof target.addEventListener !== "function") return () => {};

  const onKeyDown = (event) => {
    handleEscapeKeydown(event);
  };

  let attached = false;
  const syncToDepth = (count) => {
    const wanted = count > 0;
    if (wanted === attached) return;
    attached = wanted;
    if (wanted) target.addEventListener("keydown", onKeyDown);
    else target.removeEventListener("keydown", onKeyDown);
  };

  const unsubscribe = subscribeDismissibles(syncToDepth);
  // Subscribing only reports CHANGES, and overlays already open when this
  // installs (child effects run before the parent's) would otherwise never be
  // announced. Read the depth once to catch up.
  syncToDepth(dismissibleCount());

  return () => {
    unsubscribe();
    syncToDepth(0);
  };
};

/**
 * Mount point. One call, from the app shell — this is a document-level key
 * handler, and more than one of them is the double-dismiss bug by construction.
 */
export const useEscapeToDismiss = () => {
  useEffect(() => installEscapeToDismiss(), []);
};

export default useEscapeToDismiss;
